import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBoundStore } from '@Store/index';
import { HttpService } from '@Services/index';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import type { ServerErrorResponse } from '@Types/index';
import type { UboFileData } from './useFileManagement';

interface IProps {
  llcFiles: FormData[];
  uboFiles: UboFileData[];
  handleReset: () => void;
  reportErrorDuringFileUpload: (
    errorCode: string,
    httpStatus: number,
    errorMessage: string
  ) => void;
}

export const useFileUpload = ({
  llcFiles,
  uboFiles,
  handleReset,
  reportErrorDuringFileUpload,
}: IProps) => {
  const { navigateToSpecificPage } = useNavigationManager();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => uploadFilesAndSubmitTokens(llcFiles, uboFiles),
    mutationKey: ['getFileTokens'],
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['getKybData'] });
      navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
    },
    onError: (error: ServerErrorResponse) => {
      const error0 = error?.response?.data?.errors?.[0];
      if (error0) {
        reportErrorDuringFileUpload(String(error0.codeName), error0.code, error0.message);
      }
      handleReset();
    },
  });
};

export const uploadFilesAndSubmitTokens = async (llcFiles: FormData[], uboFiles: UboFileData[]) => {
  const { customerId } = useBoundStore.getState().customer;
  const {
    getKybData,
    onboardingSlice: { getIsEmbedded },
  } = useBoundStore.getState();
  const kybData = getKybData();

  // Step 1: Upload and submit Primary/LLC documents (all together in parallel)
  if (llcFiles.length > 0) {
    const llcTokenPromises = llcFiles.map(async (file) => {
      const fileType = file.get('fileType');
      const url = getIsEmbedded()
        ? `v4/secureonboarding/partner/user/${customerId}/llc/upload?fileType=${fileType}`
        : `v4/secureonboarding/user/${customerId}/llc/upload?fileType=${fileType}`;
      file.delete('fileType');

      const { data } = await HttpService.post(url, file, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    });

    const llcTokens = await Promise.all(llcTokenPromises);
    const llcTokensToSend = llcTokens.map((token) => token.fileId);

    const tokenUploadUrl = getIsEmbedded()
      ? `v3/secureonboarding/partner/user/${customerId}/llc/${kybData.business.externalId}/submitDocs`
      : `v3/secureonboarding/user/${customerId}/llc/${kybData.business.externalId}/submitDocs`;

    await HttpService.post(tokenUploadUrl, {
      documentTokens: llcTokensToSend,
    });
  }

  // Step 2: Handle UBO documents if they exist
  if (uboFiles.length > 0) {
    // Upload each UBO file separately and collect fileIds
    const uboItems = [];
    for (const uboFile of uboFiles) {
      const url = getIsEmbedded()
        ? `v4/secureonboarding/partner/user/${customerId}/llc/upload?fileType=${uboFile.fileType}`
        : `v4/secureonboarding/user/${customerId}/llc/upload?fileType=${uboFile.fileType}`;
      uboFile.formData.delete('fileType');
      const { data } = await HttpService.post(url, uboFile.formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      uboItems.push({
        customerExternalId: uboFile.externalId,
        fileId: data.fileId,
      });
    }

    // Submit UBO documents tokens
    const tokenUploadUrl = getIsEmbedded()
      ? `v3/secureonboarding/partner/user/${customerId}/llc/${kybData.business.externalId}/submitDocs`
      : `v3/secureonboarding/user/${customerId}/llc/${kybData.business.externalId}/submitDocs`;

    await HttpService.post(tokenUploadUrl, {
      documentTokens: uboItems.map((item) => item.fileId),
    });

    // Step 4: Call new V4 kyc endpoint
    const v4Endpoint = getIsEmbedded()
      ? `v4/secureonboarding/partner/user/${customerId}/llc/kyc`
      : `v4/secureonboarding/user/${customerId}/llc/kyc`;

    await HttpService.post(v4Endpoint, { items: uboItems });
  }
};
